import { Box, Button, FormControl, FormHelperText, Paper, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import React, { useMemo } from 'react';

interface HourDateTimeSelectProps {
    label?: string;
    value: Date | null;
    onChange: (date: Date | null) => void;
    error?: string;
    required?: boolean;
    minDate?: Date; // earliest selectable date
    maxDate?: Date; // latest selectable date
    disabled?: boolean;
    startHour?: number; // business hours start inclusive
    endHour?: number;   // business hours end exclusive
    stepHours?: number; // step size in hours
    disablePast?: boolean; // disable hours earlier than now when selected date is today
    autoFocusHour?: boolean; // when date changes, keep previous hour if possible
}

/**
 * Custom hour-only picker combining a DatePicker with a clean hour grid.
 * Provides clearer UX than MUI's DateTimePicker when minutes aren't needed.
 */
const HourDateTimeSelect: React.FC<HourDateTimeSelectProps> = ({
    label = 'Select Date & Hour',
    value,
    onChange,
    error,
    required = true,
    minDate,
    maxDate,
    disabled = false,
    startHour = 6,
    endHour = 22,
    stepHours = 1,
    disablePast = true,
    autoFocusHour = true,
}) => {
    const datePart = value ? new Date(value) : null;

    const handleDateChange = (newDate: Date | null) => {
        if (!newDate) {
            onChange(null);
            return;
        }
        // Preserve hour if possible
        const hour = value ? value.getHours() : Math.min(startHour, endHour - 1);
        const adjusted = new Date(newDate);
        adjusted.setHours(hour, 0, 0, 0);
        onChange(adjusted);
    };

    const now = new Date();

    const hours = useMemo(() => {
        const list: number[] = [];
        for (let h = startHour; h < endHour; h += stepHours) {
            list.push(h);
        }
        return list;
    }, [startHour, endHour, stepHours]);

    const isHourDisabled = (hour: number) => {
        if (!datePart) return true; // no date selected yet
        if (minDate && datePart < stripTime(minDate)) return true;
        if (maxDate && datePart > stripTime(maxDate)) return true;
        if (disablePast) {
            if (isSameDay(datePart, now) && hour < now.getHours()) return true;
        }
        return false;
    };

    const selectedHour = value ? value.getHours() : null;

    const handleHourClick = (hour: number) => {
        if (!datePart) return;
        if (isHourDisabled(hour)) return;
        const updated = new Date(datePart);
        updated.setHours(hour, 0, 0, 0);
        onChange(updated);
    };

    return (
        <FormControl fullWidth error={!!error} required={required} disabled={disabled}>
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>{label}</Typography>
                <DatePicker
                    value={datePart}
                    onChange={handleDateChange}
                    minDate={minDate}
                    maxDate={maxDate}
                    disabled={disabled}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
                <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {hours.map(h => {
                            const disabledHour = isHourDisabled(h);
                            const active = selectedHour === h;
                            const display = formatHour(h);
                            return (
                                <Button
                                    key={h}
                                    variant={active ? 'contained' : 'outlined'}
                                    color={active ? 'primary' : 'inherit'}
                                    size="small"
                                    disabled={disabledHour}
                                    onClick={() => handleHourClick(h)}
                                    sx={{
                                        minWidth: 64,
                                        fontSize: 12,
                                        py: 1,
                                        borderRadius: 2,
                                        textTransform: 'none'
                                    }}
                                >
                                    {display}
                                </Button>
                            );
                        })}
                    </Box>
                </Box>
            </Paper>
            {error && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
    );
};

// Helpers
function formatHour(h: number): string {
    const suffix = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    return `${hour12} ${suffix}`;
}

function stripTime(d: Date): Date {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
}

function isSameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default HourDateTimeSelect;
